import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { ExternalLink, Monitor, Smartphone } from 'lucide-react';

import { tx } from './i18n';
import { storefrontOrigin } from './ProductPageLive';

// A Longevity Hub page as the store draws it, beside the fields it is written
// in.
//
// The storefront shows it in a frame (its src/site/editBridge.js), but reads
// no page of its own there: this hands it the page being written (`sent`: the
// page, the Hub's list and the words by key in the language being written),
// and again on every change, so the frame always shows what is in the
// portal, saved or not, also for a page that is not published. Pointing at a
// part of the page tells the portal which field, section and entry it is
// (`onPick`); the part being written is marked there (`focus`). The frame
// says how tall the page is and is made that tall, so the page is read with
// the portal's own scroll and has none of its own. A frame as tall as its
// page has no window height for the page to size its parts by, so the page is
// told the height of the window it is drawn for (`window`). Nothing is saved
// through the frame. Over the frame: where the page is on the store
// (`address`), and the way to it there once it is shown (`href`).
const VIEWS = { phone: { width: 390, scale: 1, window: 844 }, computer: { width: 1280, scale: 0.42, window: 800 } };
const READY_MS = 15000;
const HAND_OVER_MS = 180;

export default function HubPageLive({
  kind, sent, focus, onPick, address = '', href = '',
}) {
  const frame = useRef(null);
  const origin = useRef(null);
  const latest = useRef(sent);
  const picked = useRef(onPick);
  latest.current = sent;
  picked.current = onPick;
  const [view, setView] = useState('phone');
  const shown = useRef(view);
  shown.current = view;
  // 'waiting' until the page says it is drawn, 'ready', or 'silent' when it never does.
  const [state, setState] = useState('waiting');
  // How tall the page says it is; none until it has said so.
  const [tall, setTall] = useState(0);

  const tell = useCallback((message) => {
    if (origin.current && frame.current?.contentWindow) {
      frame.current.contentWindow.postMessage({ source: 'ph-portal', ...message }, origin.current);
    }
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.source !== frame.current?.contentWindow) return;
      const message = event.data;
      if (!message || message.source !== 'ph-storefront') return;
      origin.current = event.origin;
      if (message.type === 'hub-wanted') {
        tell({ type: 'viewport', height: VIEWS[shown.current].window });
        tell({ type: 'hub-page', ...latest.current });
      } else if (message.type === 'ready') setState('ready');
      else if (message.type === 'page-size' && Number(message.height) > 0) setTall(Number(message.height));
      else if (message.type === 'pick') picked.current({ field: message.field || '', section: message.section, item: message.item });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [tell]);

  // A page that never answers (a storefront without the preview) is said to be silent.
  useEffect(() => {
    if (state !== 'waiting') return undefined;
    const timer = window.setTimeout(() => setState((current) => (current === 'waiting' ? 'silent' : current)), READY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  // Every change is handed over a moment after the last key, so typing stays light.
  useEffect(() => {
    if (state !== 'ready') return undefined;
    const timer = window.setTimeout(() => tell({ type: 'hub-page', ...latest.current }), HAND_OVER_MS);
    return () => window.clearTimeout(timer);
  }, [sent, state, tell]);

  useEffect(() => {
    if (state === 'ready' && focus) tell({ type: 'focus', ...focus });
  }, [focus, state, tell]);

  // The window the page is drawn for is said again when the view changes.
  useEffect(() => {
    if (state === 'ready') tell({ type: 'viewport', height: VIEWS[view].window });
  }, [state, tell, view]);

  const { width, scale } = VIEWS[view];
  return <aside className="xo-pm-live xo-hub-live" aria-label={tx('ui.hub.live.title')}>
    <div className="xo-pm-live-head">
      <span>
        <strong>{tx('ui.hub.live.title')}</strong>
        {state === 'silent' && <small>{tx('ui.hub.live.silent')}</small>}
      </span>
      <div className="xo-pm-live-views" role="radiogroup" aria-label={tx('ui.products.page.live.view')}>
        <button type="button" role="radio" aria-checked={view === 'phone'} className={view === 'phone' ? 'is-on' : ''}
          aria-label={tx('ui.products.page.live.phone')} title={tx('ui.products.page.live.phone')}
          onClick={() => setView('phone')}><Smartphone aria-hidden="true" /></button>
        <button type="button" role="radio" aria-checked={view === 'computer'} className={view === 'computer' ? 'is-on' : ''}
          aria-label={tx('ui.products.page.live.computer')} title={tx('ui.products.page.live.computer')}
          onClick={() => setView('computer')}><Monitor aria-hidden="true" /></button>
      </div>
    </div>
    {address !== '' && <div className="xo-hub-live-address">
      <span dir="ltr" title={address}>{address}</span>
      {href !== '' && <a className="xo-btn-2" href={href} target="_blank" rel="noopener noreferrer">
        <ExternalLink aria-hidden="true" />{tx('ui.hub.page.open')}</a>}
    </div>}
    <div className={`xo-pm-live-stage is-${view}`}
      style={{ '--live-width': `${width}px`, '--live-scale': scale, ...(tall > 0 ? { height: `${Math.ceil(tall * scale)}px` } : {}) }}>
      <iframe ref={frame} title={tx('ui.hub.live.title')} scrolling="no" style={tall > 0 ? { height: `${tall}px` } : undefined}
        src={`${storefrontOrigin()}/hub/preview/?ph-edit=1&kind=${kind === 'front' ? 'front' : 'article'}`} />
    </div>
  </aside>;
}
